/**
 * Helpers to categorize French grades into OFA France's 3 academic levels.
 *
 * Levels (used for Inscriptions admin page):
 *   - primaire  (GS → CM2)       — small classes (4 max)
 *   - college   (6e → 4e)        — medium (6 max)
 *   - lycee     (3e → Terminale) — larger (8 max)
 *
 * For Scheduling page, the user prefers "Élémentaire" instead of "Primaire" — same range.
 */

const PRIMAIRE = ["gs", "cp", "ce1", "ce2", "cm1", "cm2"];
const COLLEGE = ["6e", "5e", "4e", "6ème", "5ème", "4ème"];
const LYCEE = ["3e", "3ème", "2nde", "2de", "1ère", "1ere", "1re", "tle", "terminale", "term"];

const normalize = (g) => (g || "").trim().toLowerCase().replace(/\s+/g, "");

export function gradeLevel(grade) {
  const g = normalize(grade);
  if (!g) return "unknown";
  // Support compound grades (e.g. "CP-CE1"): split and pick the broadest band
  const parts = g.split(/[\-/]+/).filter(Boolean);
  const has = (pool) => parts.some(p => pool.includes(p));
  if (has(LYCEE)) return "lycee";
  if (has(COLLEGE)) return "college";
  if (has(PRIMAIRE)) return "primaire";
  return "unknown";
}

// Ordering used to sort grade keys inside each level
const GRADE_ORDER = [
  "GS", "CP", "CE1", "CE2", "CM1", "CM2",
  "6e", "5e", "4e", "3e",
  "2nde", "1ère", "Terminale",
];

export function sortGradeKeys(keys) {
  return [...keys].sort((a, b) => {
    const ia = GRADE_ORDER.findIndex(o => normalize(o) === normalize(a));
    const ib = GRADE_ORDER.findIndex(o => normalize(o) === normalize(b));
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

export const LEVEL_LABELS = {
  primaire: {
    fr: { title: "Primaire", range: "GS → CM2", short: "Primaire" },
    en: { title: "Primary", range: "GS → CM2", short: "Primary" },
  },
  college: {
    fr: { title: "Collège", range: "6e → 4e", short: "Collège" },
    en: { title: "Middle school", range: "6th → 8th", short: "Middle" },
  },
  lycee: {
    fr: { title: "Lycée", range: "3e → Terminale", short: "Lycée" },
    en: { title: "High school", range: "9th → 12th", short: "HS" },
  },
  unknown: {
    fr: { title: "Non classé", range: "Sans niveau", short: "—" },
    en: { title: "Unclassified", range: "No grade", short: "—" },
  },
};

// Alternative label set for the Scheduling page ("Élémentaire" instead of "Primaire")
export const SCHEDULE_LEVEL_LABELS = {
  ...LEVEL_LABELS,
  primaire: {
    fr: { title: "Élémentaire", range: "GS → CM2", short: "Élémentaire" },
    en: { title: "Elementary", range: "GS → CM2", short: "Elementary" },
  },
};

export const LEVEL_ORDER = ["primaire", "college", "lycee", "unknown"];
